import { useState, useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Button } from '../../../components/ui/Button';
import type { VenueTypeConfig } from '@lov/shared';
import type { FilterState } from '../hooks/useVenueFilters';
import { useVenueTypes } from '../hooks/useVenueTypes';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onApply: () => void;
  onClear: () => void;
  resultCount?: number;
  states?: Array<{ id: string; name: string }>;
  cities?: Array<{ id: string; name: string; stateId: string }>;
}

const DrawerContainer = styled(Box)(({ theme }) => ({
  width: '100vw',
  height: 'calc(100vh - 56px)',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  overflow: 'hidden',
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.divider,
  },
}));

const DrawerContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: 24,
  paddingBottom: 100,
});

const DrawerFooter = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: 16,
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: 12,
  marginTop: 24,
  '&:first-of-type': {
    marginTop: 0,
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const PriceGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
});

export function FilterDrawer({
  open,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onClear,
  resultCount,
  states = [],
  cities = [],
}: FilterDrawerProps) {
  const { data: venueTypes } = useVenueTypes();
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof FilterState, newValue: string | number | undefined) => {
    const updated = { ...localFilters, [key]: newValue || undefined };

    // Reset city when state changes
    if (key === 'stateId') {
      updated.cityId = undefined;
    }

    setLocalFilters(updated);
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApply();
  };

  const handleClear = () => {
    const clearedFilters: FilterState = { page: 1 };
    setLocalFilters(clearedFilters);
    onClear();
  };

  const filteredCities = localFilters.stateId
    ? cities.filter((city) => city.stateId === localFilters.stateId)
    : cities;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          maxHeight: 'calc(100vh - 56px)',
        },
      }}
    >
      <DrawerContainer>
        <DrawerHeader>
          <Box sx={{ width: 40 }} /> {/* Spacer for alignment */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontWeight: 600,
              fontSize: '1.125rem',
            }}
          >
            Filters
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <DrawerContent>
          {/* Search */}
          <SectionLabel>Search</SectionLabel>
          <StyledTextField
            fullWidth
            size="small"
            placeholder="Search venues..."
            value={localFilters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Venue Type */}
          <SectionLabel>Venue Type</SectionLabel>
          <FormControl fullWidth size="small">
            <InputLabel>Select Type</InputLabel>
            <StyledSelect
              value={localFilters.venueType || ''}
              label="Select Type"
              onChange={(e) => handleFilterChange('venueType', e.target.value === '' ? undefined : String(e.target.value))}
            >
              <MenuItem value="">All Types</MenuItem>
              {venueTypes
                .filter((vt: VenueTypeConfig) => vt.isActive)
                .sort((a: VenueTypeConfig, b: VenueTypeConfig) => a.displayOrder - b.displayOrder)
                .map((type: VenueTypeConfig) => (
                  <MenuItem key={type.code} value={type.code}>
                    {type.name}
                  </MenuItem>
                ))}
            </StyledSelect>
          </FormControl>

          {/* Location */}
          <SectionLabel>Location</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <StyledSelect
                value={localFilters.stateId || ''}
                label="State"
                onChange={(e) => handleFilterChange('stateId', e.target.value as string)}
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!localFilters.stateId}>
              <InputLabel>City</InputLabel>
              <StyledSelect
                value={localFilters.cityId || ''}
                label="City"
                onChange={(e) => handleFilterChange('cityId', e.target.value as string)}
              >
                <MenuItem value="">All Cities</MenuItem>
                {filteredCities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>

          {/* Price Range */}
          <SectionLabel>Price Range</SectionLabel>
          <PriceGrid>
            <StyledTextField
              fullWidth
              size="small"
              type="number"
              label="Min Price"
              placeholder="0"
              value={localFilters.minPrice || ''}
              onChange={(e) =>
                handleFilterChange('minPrice', e.target.value ? Number(e.target.value) : undefined)
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
            <StyledTextField
              fullWidth
              size="small"
              type="number"
              label="Max Price"
              placeholder="10000"
              value={localFilters.maxPrice || ''}
              onChange={(e) =>
                handleFilterChange('maxPrice', e.target.value ? Number(e.target.value) : undefined)
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </PriceGrid>

          <Divider sx={{ my: 3 }} />

          {/* Clear Filters Button */}
          <Button variant="outline" fullWidth onClick={handleClear}>
            Clear All Filters
          </Button>
        </DrawerContent>

        <DrawerFooter
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
          })}
        >
          <Button
            variant="primary"
            fullWidth
            size="lg"
            onClick={handleApply}
            style={{ fontSize: '1rem', fontWeight: 600 }}
          >
            {resultCount !== undefined ? `Show ${resultCount} Venues` : 'Apply Filters'}
          </Button>
        </DrawerFooter>
      </DrawerContainer>
    </Drawer>
  );
}
